/**
 * Campo del gruppo (nome o descrizione) modificabile sul posto dagli amministratori.
 */
import { useEffect, useState } from 'react';
import { Pencil, Loader2 } from 'lucide-react';
import { iconTip } from '@/components/messages/ChatTooltip';

interface Props {
  label: string;
  value: string;
  placeholder: string;
  canEdit: boolean;
  /** Motivo mostrato sulla matita disattivata. */
  disabledReason?: string | null;
  multiline?: boolean;
  maxLength: number;
  busy?: boolean;
  onSave: (value: string) => Promise<boolean>;
  /** Nome del gruppo: testo grande e centrato. */
  heading?: boolean;
}

export function GroupEditableField({ label, value, placeholder, canEdit, disabledReason, multiline, maxLength, busy, onSave, heading }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);

  const save = async () => {
    const v = draft.trim();
    if (v === value.trim()) { setEditing(false); return; }
    if (heading && !v) return;
    if (await onSave(v)) setEditing(false);
  };

  if (editing) {
    const cls = 'w-full rounded-md border border-[#2383E2] bg-white dark:bg-white/5 px-2 py-1.5 text-sm text-[#37352F] dark:text-white focus:outline-none';
    return (
      <div className="space-y-2 text-left">
        {multiline ? (
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={maxLength} rows={4} aria-label={label} className={cls} autoFocus />
        ) : (
          <input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={maxLength} aria-label={label} className={cls} autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') void save(); if (e.key === 'Escape') setEditing(false); }} />
        )}
        <div className="flex items-center justify-end gap-2">
          <span className="mr-auto text-[11px] text-[#787774]">{draft.length}/{maxLength}</span>
          <button type="button" onClick={() => setEditing(false)} disabled={busy} className="px-2.5 py-1 text-xs rounded-md hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 text-[#37352F] dark:text-white">Annulla</button>
          <button type="button" onClick={() => void save()} disabled={busy || (heading && !draft.trim())}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-[#2383E2] text-white disabled:opacity-50">
            {busy && <Loader2 className="w-3 h-3 animate-spin" />}Salva
          </button>
        </div>
      </div>
    );
  }

  const pencil = (
    <button type="button" onClick={() => setEditing(true)} disabled={!canEdit}
      className="p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
      {...iconTip(canEdit ? `Modifica ${label.toLowerCase()}` : disabledReason || `Non puoi modificare ${label.toLowerCase()}`)}>
      <Pencil className="w-3.5 h-3.5 text-[#787774]" />
    </button>
  );

  if (heading) {
    return (
      <div className="flex items-center justify-center gap-1">
        <h2 className="font-semibold text-lg text-[#37352F] dark:text-white break-words">{value || placeholder}</h2>
        {pencil}
      </div>
    );
  }
  return (
    <div className="text-left">
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-xs text-[#787774] uppercase tracking-wide">{label}</h4>
        {pencil}
      </div>
      <p className={`text-sm whitespace-pre-wrap break-words ${value ? 'text-[#37352F] dark:text-white' : 'text-[#787774] italic'}`}>{value || placeholder}</p>
    </div>
  );
}
